import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import TermsCheckbox from "@/components/auth/registration/TermsCheckbox";
import PasswordStrengthIndicator from "@/components/auth/PasswordStrengthIndicator";
import PasswordMatchIndicator from "@/components/auth/PasswordMatchIndicator";
import SecurePasswordInput from "@/components/auth/SecurePasswordInput";
import { AddressAutocomplete } from "@/components/properties/AddressAutocomplete";
import { useEmailValidation } from "@/hooks/useEmailValidation";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  companyName: string;
  phoneNumber: string;
  dealsAnnually: number;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  agreeTerms: boolean;
  smsOptIn?: boolean;
}

interface EmailValidationState {
  isValid: boolean;
  status: 'idle' | 'checking' | 'valid' | 'invalid' | 'wrong-role';
  hasBeenValidated: boolean;
}

interface BuyerPersonalInfoSectionProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  passwordStrength: any;
  onEmailValidationChange?: (validation: EmailValidationState) => void;
}

const BuyerPersonalInfoSection = ({ 
  formData, 
  updateFormData,
  passwordStrength,
  onEmailValidationChange
}: BuyerPersonalInfoSectionProps) => {
  const { validationState, validateEmail, getValidationMessageData } = useEmailValidation('buyer');
  const messageData = getValidationMessageData();

  // Notify parent of validation state changes
  const notifyValidationChange = (status: 'idle' | 'checking' | 'valid' | 'invalid' | 'wrong-role') => {
    if (status !== 'idle' && status !== 'checking') {
      onEmailValidationChange?.({
        isValid: status === 'valid',
        status: status,
        hasBeenValidated: true
      });
    }
  };

  // Update parent when validation state changes
  if (validationState.status !== 'idle' && validationState.status !== 'checking') {
    setTimeout(() => notifyValidationChange(validationState.status), 0);
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-[#ffd700] mb-4">Account & Personal Information</h3>
      
      {/* Email */}
      <div>
        <Label htmlFor="email" className="text-white">Email Address</Label>
        <div className="relative">
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData({ email: e.target.value })}
            onBlur={async () => {
              await validateEmail(formData.email);
              // Fire-and-forget: Save to pending_registrations
              if (formData.email && formData.email.includes('@')) {
                supabase.functions.invoke('save-pending-registration', {
                  body: { 
                    email: formData.email, 
                    user_type: 'buyer',
                    full_name: formData.fullName || undefined,
                    phone_number: formData.phoneNumber || undefined
                  }
                }).catch(() => {}); // Silent failure
              }
            }}
            className="bg-gray-700 border-gray-600 text-white pr-10"
            required
            aria-describedby={validationState.status === 'invalid' ? 'email-error' : undefined}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {validationState.status === 'checking' && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
            {validationState.status === 'valid' && (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            {(validationState.status === 'invalid' || validationState.status === 'wrong-role') && (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
          </div>
        </div>
        {messageData?.type === 'wrong-role' && (
          <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-3 mt-2">
            <p className="text-sm text-yellow-500 font-medium">
              This email is registered as a {messageData.roleName} account
            </p>
            <p className="text-xs text-gray-400 mt-1">
              To access {messageData.currentRoleName} features, please use a different email address. Each email can only be associated with one role.
            </p>
            <Link to={messageData.loginPath} className="text-xs text-[#ffd700] hover:underline mt-1 inline-block">
              Already a {messageData.roleName}? Login here →
            </Link>
          </div>
        )}
        {messageData?.type === 'invalid' && (
          <p id="email-error" className="text-sm text-red-400 mt-1" role="alert">
            {messageData.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <Label htmlFor="password" className="text-white">Password</Label>
        <SecurePasswordInput
          id="password"
          value={formData.password}
          onChange={(e) => updateFormData({ password: e.target.value })}
          className="bg-gray-700 border-gray-600 text-white"
          required
        />
        {formData.password && (
          <PasswordStrengthIndicator 
            password={formData.password}
            personalInfo={{
              name: formData.fullName,
              email: formData.email,
              company: formData.companyName
            }}
          />
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
        <SecurePasswordInput
          id="confirmPassword"
          value={formData.confirmPassword}
          onChange={(e) => updateFormData({ confirmPassword: e.target.value })}
          className="bg-gray-700 border-gray-600 text-white"
          required
        />
        <PasswordMatchIndicator 
          password={formData.password} 
          confirmPassword={formData.confirmPassword} 
        />
      </div>

      {/* Full Name */}
      <div>
        <Label htmlFor="fullName" className="text-white">Full Name</Label>
        <Input
          id="fullName"
          type="text"
          value={formData.fullName}
          onChange={(e) => updateFormData({ fullName: e.target.value })}
          className="bg-gray-700 border-gray-600 text-white"
          required
        />
      </div>

      {/* Company Name (Optional) */}
      <div>
        <Label htmlFor="companyName" className="text-white">Company Name (Optional)</Label>
        <Input
          id="companyName"
          type="text"
          value={formData.companyName}
          onChange={(e) => updateFormData({ companyName: e.target.value })}
          className="bg-gray-700 border-gray-600 text-white"
        />
      </div>

      {/* Phone Number */}
      <div>
        <Label htmlFor="phoneNumber" className="text-white">Phone Number</Label>
        <PhoneInput
          id="phoneNumber"
          value={formData.phoneNumber}
          onChange={(value) => updateFormData({ phoneNumber: value })}
          className="bg-gray-700 border-gray-600 text-white"
          required
          showValidation={true}
        />
      </div>

      {/* SMS Opt-In Checkbox - TCPA Compliance */}
      <div className="bg-gray-800/50 p-3 rounded-md border border-gray-600">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="smsOptIn"
            checked={formData.smsOptIn || false}
            onCheckedChange={(checked) => updateFormData({ smsOptIn: checked as boolean })}
            className="border-gray-500"
          />
          <div className="grid gap-1.5 leading-none">
            <Label
              htmlFor="smsOptIn"
              className="text-sm font-medium text-white leading-none cursor-pointer"
            >
              I agree to receive text messages about new properties and account updates (Optional)
            </Label>
            <p className="text-xs text-gray-400">
              By checking this box, you consent to receive informational and transactional SMS notifications about one-time passcodes, property activity, account updates, and platform features from Go Dyspo. Message and data rates may apply. Message frequency may vary. Reply STOP to opt out at any time. View our Privacy Policy at{" "}
              <a 
                href="https://godyspo.com/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#ffd700] hover:underline"
              >
                https://godyspo.com/privacy-policy
              </a>.
            </p>
          </div>
        </div>
      </div>

      {/* Deals Annually */}
      <div>
        <Label htmlFor="dealsAnnually" className="text-white">Number of Deals Annually</Label>
        <Input
          id="dealsAnnually"
          type="number"
          min="0"
          value={formData.dealsAnnually}
          onChange={(e) => updateFormData({ dealsAnnually: parseInt(e.target.value) || 0 })}
          className="bg-gray-700 border-gray-600 text-white"
          required
        />
      </div>

      {/* Address Fields */}
      <div>
        <AddressAutocomplete
          initialAddress={formData.address}
          onAddressSelect={(place) => {
            updateFormData({
              address: place.address || '',
              city: place.city || '',
              state: place.state || '',
              zipCode: place.zipCode || ''
            });
          }}
          onInputChange={(value) => updateFormData({ address: value })}
          showLabel={true}
          label="Street Address"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="city" className="text-white">City</Label>
          <Input
            id="city"
            type="text"
            placeholder="City"
            value={formData.city}
            onChange={(e) => updateFormData({ city: e.target.value })}
            className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
            required
          />
        </div>
        <div>
          <Label htmlFor="state" className="text-white">State</Label>
          <Input
            id="state"
            type="text"
            placeholder="State"
            value={formData.state}
            onChange={(e) => updateFormData({ state: e.target.value.toUpperCase().slice(0, 2) })}
            className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
            maxLength={2}
            required
          />
        </div>
        <div>
          <Label htmlFor="zipCode" className="text-white">ZIP Code</Label>
          <Input
            id="zipCode"
            type="text"
            placeholder="ZIP"
            value={formData.zipCode}
            onChange={(e) => updateFormData({ zipCode: e.target.value })}
            className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
            maxLength={10}
            required
          />
        </div>
      </div>

      {/* Terms Checkbox */}
      <TermsCheckbox
        agreeTerms={formData.agreeTerms}
        setAgreeTerms={(agreed) => updateFormData({ agreeTerms: agreed })}
      />
    </div>
  );
};

export default BuyerPersonalInfoSection;
